import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Select, Switch } from "antd";
import { Pencil, Trash2 } from "lucide-react";
import Table from "components/Table";
import ConfirmationModal from "pages/data-table-view/components/ConfirmationModal";
import Pagination from "pages/data-table-view/components/Pagination";
import SearchInput from "pages/data-table-view/components/SearchInput";
import Toast from "pages/data-table-view/components/Toast";
import useHttp from "hooks/useHttp";
import {
  CANONICAL_BY_DIMENSION,
  DIMENSION_TYPES,
  UTM_FIELD_BY_DIMENSION,
  UTM_FIELDS,
  createAttributionMapping,
  deleteAttributionMapping,
  getAttributionMappings,
  updateAttributionMapping,
} from "services/attribution-mapping.service";

const EMPTY_FORM = {
  dimensionType: "platform",
  utmField: "source",
  rawValue: "",
  canonicalValue: "Instagram",
  priority: 0,
  isActive: true,
};

const SESSION_KEY = "attribution_mapping_filters";

function labelFor(value, options) {
  return options.find((o) => o.value === value)?.label ?? value;
}

export default function AttributionMappingTable() {
  const api = useHttp();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [filters, setFilters] = useState({
    dimensionType: undefined,
    utmField: undefined,
    isActive: undefined,
  });

  const canonicalOptions = useMemo(
    () => CANONICAL_BY_DIMENSION[formData.dimensionType] ?? [],
    [formData.dimensionType]
  );

  const isFormValid =
    formData.rawValue.trim() !== "" &&
    formData.canonicalValue &&
    canonicalOptions.includes(formData.canonicalValue);

  const fetchMappings = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery || undefined,
        dimensionType: filters.dimensionType,
        utmField: filters.utmField,
        isActive: filters.isActive,
      };
      const { records, count } = await getAttributionMappings(api, params);
      setData(records);
      setTotalRecords(count);
      setError(null);
    } catch {
      setError("Failed to fetch attribution mappings.");
      setToast({ show: true, message: "Failed to fetch attribution mappings.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [api, currentPage, itemsPerPage, searchQuery, filters]);

  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) setFilters(JSON.parse(saved));
  }, []);

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleCreateClick = () => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setIsFormOpen(true);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setFormData({
      dimensionType: item.dimensionType,
      utmField: item.utmField,
      rawValue: item.rawValue,
      canonicalValue: item.canonicalValue,
      priority: item.priority ?? 0,
      isActive: item.isActive ?? true,
    });
    setIsFormOpen(true);
  };

  const handleFormSubmit = async () => {
    const payload = {
      ...formData,
      rawValue: formData.rawValue.trim(),
      priority: Number(formData.priority) || 0,
    };

    try {
      if (editingItem) {
        await updateAttributionMapping(api, editingItem._id, payload);
        setToast({ show: true, message: "Mapping updated", type: "success" });
      } else {
        await createAttributionMapping(api, payload);
        setToast({ show: true, message: "Mapping created", type: "success" });
      }
      setIsFormOpen(false);
      fetchMappings();
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to save attribution mapping";
      setToast({ show: true, message, type: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await deleteAttributionMapping(api, itemToDelete._id);
      setToast({ show: true, message: "Mapping deleted", type: "success" });
      fetchMappings();
    } catch {
      setToast({ show: true, message: "Failed to delete mapping", type: "error" });
    } finally {
      setItemToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const columns = [
    {
      key: "number",
      label: "No.",
      render: (_item, idx) => (currentPage - 1) * itemsPerPage + idx + 1,
    },
    {
      key: "dimensionType",
      label: "Dimension",
      render: (item) => labelFor(item.dimensionType, DIMENSION_TYPES),
    },
    {
      key: "utmField",
      label: "UTM Field",
      render: (item) => labelFor(item.utmField, UTM_FIELDS),
    },
    { key: "rawValue", label: "Raw UTM Value" },
    { key: "canonicalValue", label: "Canonical Value" },
    { key: "priority", label: "Priority" },
    {
      key: "isActive",
      label: "Active",
      render: (item) => (item.isActive ? "Yes" : "No"),
    },
    {
      key: "updatedAt",
      label: "Updated",
      render: (item) =>
        item.updatedAt ? new Date(item.updatedAt).toLocaleString("en-US") : "-",
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <div className="flex gap-2">
          <button onClick={() => handleEditClick(item)} aria-label="Edit">
            <Pencil className="w-4 h-4 text-blue-600" />
          </button>
          <button
            onClick={() => {
              setItemToDelete(item);
              setShowDeleteModal(true);
            }}
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalRecords);

  return (
    <div className="bg-background">
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        UTM source → platform, campaign → account (Nico, Jordi, BV1), medium → entry point.{" "}
        Mappings apply to{" "}
        <Link to="/analytics/attribution" className="font-medium underline">
          Attribution
        </Link>
        . Changes apply immediately - no deploy or seed script required.
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex-1">
              <SearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                onClear={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                placeholder="Search raw or canonical values"
              />
            </div>
            <Select
              allowClear
              placeholder="Dimension"
              className="min-w-[140px]"
              value={filters.dimensionType}
              onChange={(val) => {
                setFilters((f) => ({ ...f, dimensionType: val }));
                setCurrentPage(1);
              }}
              options={DIMENSION_TYPES}
            />
            <Select
              allowClear
              placeholder="UTM Field"
              className="min-w-[140px]"
              value={filters.utmField}
              onChange={(val) => {
                setFilters((f) => ({ ...f, utmField: val }));
                setCurrentPage(1);
              }}
              options={UTM_FIELDS}
            />
            <Select
              allowClear
              placeholder="Active"
              className="min-w-[100px]"
              value={filters.isActive}
              onChange={(val) => {
                setFilters((f) => ({ ...f, isActive: val }));
                setCurrentPage(1);
              }}
              options={[
                { label: "Active", value: true },
                { label: "Inactive", value: false },
              ]}
            />
            <button
              onClick={handleCreateClick}
              className="bg-primary-600 text-white px-4 py-1.5 rounded hover:bg-primary-700 text-sm h-8"
            >
              Add Mapping
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <Select
                className="!w-[70px]"
                value={itemsPerPage}
                onChange={(val) => {
                  setItemsPerPage(val);
                  setCurrentPage(1);
                }}
                options={[10, 25, 50, 100].map((n) => ({ label: n, value: n }))}
              />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          onRetry={fetchMappings}
          emptyMessage="No attribution mappings found."
        />

        {!isLoading && !error && data.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalRecords}
            itemsPerPage={itemsPerPage}
            showingFrom={indexOfFirstItem + 1}
            showingTo={indexOfLastItem}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Attribution Mapping"
        message={`Delete mapping "${itemToDelete?.rawValue}" → "${itemToDelete?.canonicalValue}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        danger
      />

      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingItem ? "Edit Attribution Mapping" : "Add Attribution Mapping"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimension <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full"
                  value={formData.dimensionType}
                  onChange={(val) => {
                    const firstCanonical = CANONICAL_BY_DIMENSION[val]?.[0] ?? "";
                    setFormData((f) => ({
                      ...f,
                      dimensionType: val,
                      utmField: UTM_FIELD_BY_DIMENSION[val] ?? f.utmField,
                      canonicalValue: firstCanonical,
                    }));
                  }}
                  options={DIMENSION_TYPES}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  UTM Field <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full"
                  value={formData.utmField}
                  onChange={(val) => setFormData((f) => ({ ...f, utmField: val }))}
                  options={UTM_FIELDS}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raw UTM Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.rawValue}
                  onChange={(e) => setFormData((f) => ({ ...f, rawValue: e.target.value }))}
                  placeholder="e.g. instagram, nico_bio"
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Stored lowercase; matched against UTM params.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Canonical Value <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full"
                  value={formData.canonicalValue}
                  onChange={(val) => setFormData((f) => ({ ...f, canonicalValue: val }))}
                  options={canonicalOptions.map((v) => ({ label: v, value: v }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData((f) => ({ ...f, priority: e.target.value }))}
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Active</span>
                <Switch
                  checked={formData.isActive}
                  onChange={(checked) => setFormData((f) => ({ ...f, isActive: checked }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsFormOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleFormSubmit}
                disabled={!isFormValid}
                className={`px-4 py-2 text-white rounded ${
                  isFormValid ? "bg-primary-600" : "bg-primary-400 cursor-not-allowed"
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}
